import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import { GET_USER_GUIDE_GROUPS } from "./gql";
import { getHardcodedUserGuideGroupIconAndColor } from "./mocks";
import { collectGroupTimeToComplete, collectOverallTimeToComplete } from "./utils";

const useUserGuideGroups = () => {
  const { data, loading } = useQuery(GET_USER_GUIDE_GROUPS);

  const completedUserGuideIds = useMemo(() => {
    return data?.completedUserGuides.map((completedGuide) => completedGuide.guide.id) ?? [];
  }, [data?.completedUserGuides]);

  const allUserGuideIds = useMemo(() => {
    return (
      data?.userGuideGroups
        .flatMap((group) => group.chapters.flatMap((chapter) => chapter.guides))
        .map((guide) => guide.id) ?? []
    );
  }, [data?.userGuideGroups]);

  // TODO: use the actual API to get the color and icon for the user guide groups
  const userGuideGroups = useMemo(
    () =>
      data?.userGuideGroups
        .map((group) => ({
          ...group,
          ...getHardcodedUserGuideGroupIconAndColor(group.id),
          timeToComplete: collectGroupTimeToComplete(group, completedUserGuideIds),
          guidesCount: group.chapters.flatMap((chapter) => chapter.guides).length,
          guidesCompletedCount: group.chapters
            .flatMap((chapter) => chapter.guides)
            .filter((guide) => completedUserGuideIds.includes(guide.id)).length,
        }))
        .sort((a, b) => a.order - b.order),
    [data?.userGuideGroups, completedUserGuideIds]
  );

  const overallTimeToComplete = useMemo(() => {
    return collectOverallTimeToComplete(data?.userGuideGroups ?? [], completedUserGuideIds);
  }, [data?.userGuideGroups, completedUserGuideIds]);

  return {
    userGuideGroups,
    userGuidesCompletedCount: completedUserGuideIds.length,
    userGuidesCount: allUserGuideIds.length,
    overallTimeToComplete,
    loading,
  };
};

export default useUserGuideGroups;
